import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { MediaAdderProps } from "./types";
import LoadingScreen from "../../../screens/LoadingScreen";
import { useNavigate } from "react-router-dom";
import { Button } from "../../misc/Button";
import DragAndDropMedia from "../../misc/DragAndDrop";
import UploadNavigationWarning from "../../misc/UploadNavigationWarning";
import { useUploadNavigationGuard } from "../../../hooks/useUploadNavigationGuard";
import { getAuthHeader } from "../../../utils/authHeader";
import { toast } from "react-toastify";
import ReconfirmOverlay from "../../misc/ReconfirmOverlay";
import PlanLimitReached from "../../misc/PlanLimitReached";

const MediaAdder: React.FC<MediaAdderProps> = ({
  isMobile,
  mlsApproved,
  propertyData,
  setPropertyData,
  setCurrentStep,
}) => {
  const navigate = useNavigate();
  const [isUploading, setIsUploading] = useState(false);
  const [activeUploads, setActiveUploads] = useState(0);
  const [showNavigationWarning, setShowNavigationWarning] = useState(false);
  const [showMissingImagesWarning, setShowMissingImagesWarning] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const [planLimitOpen, setPlanLimitOpen] = useState(false);

  // Navigation guard to prevent accidental navigation during uploads
  const { confirmNavigation, cancelNavigation } = useUploadNavigationGuard({
    uploadCount: activeUploads,
    onNavigationAttempt: () => setShowNavigationWarning(true),
  });

  // Store active upload state for refresh detection
  const hasInitiallyMounted = useRef(false);
  useEffect(() => {
    if (activeUploads > 0) {
      sessionStorage.setItem("uploadsInterrupted", "true");
    } else if (hasInitiallyMounted.current) {
      // Only remove the flag if uploads completed naturally (not on initial mount)
      sessionStorage.removeItem("uploadsInterrupted");
    }
    hasInitiallyMounted.current = true;
  }, [activeUploads]);

  useLayoutEffect(() => {
    const element = document.getElementById("media-adder");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const handleFinishClick = useCallback(() => {
    if (!propertyData) return;
    
    if (activeUploads > 0) {
      setShowNavigationWarning(true);
      setPendingNavigation(() => () => {
        const id = propertyData.id || propertyData._id.toString();
        navigate(`/listing?id=${id}`);
      });
    } else {
      const images = propertyData.images || [];
      if (images.length === 0) {
        // No images — ask the user to confirm or go upload images
        setShowMissingImagesWarning(true);
        setPendingNavigation(() => () => {
          const id = propertyData.id || propertyData._id.toString();
          navigate(`/listing?id=${id}`);
        });
      } else {
        const id = propertyData.id || propertyData._id.toString();
        navigate(`/listing?id=${id}`);
      }
    }
  }, [activeUploads, propertyData, navigate]);

  const handleProceedWithoutImages = useCallback(() => {
    setShowMissingImagesWarning(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    } else if (propertyData) {
      const id = propertyData.id || propertyData._id.toString();
      navigate(`/listing?id=${id}`);
    }
  }, [pendingNavigation, propertyData, navigate]);

  const handleUploadImages = useCallback(() => {
    setShowMissingImagesWarning(false);
    setPendingNavigation(null);
    // Keep user on page so they can upload images — focus/UX handled in DragAndDrop
  }, []);

  const refreshListing = useCallback(async () => {
    if (!propertyData) return;
    try {
      const headers = await getAuthHeader();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/listing/${propertyData._id}`, {
        method: "GET",
        headers,
      });
      if (response.ok) {
        const data = await response.json();
        setPropertyData?.(data);
      } else {
        console.error("Failed to refresh listing after upload", response.status);
      }
    } catch (err) {
      console.error("Error refreshing listing:", err);
      toast.error("Failed to refresh listing after upload.");
    }
  }, [propertyData, setPropertyData]);

  const handleLeaveWithUploads = useCallback(() => {
    sessionStorage.removeItem("uploadsInterrupted");
    setShowNavigationWarning(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
    confirmNavigation();
  }, [pendingNavigation, confirmNavigation]);

  const handleStayOnPage = useCallback(() => {
    setShowNavigationWarning(false);
    setPendingNavigation(null);
    cancelNavigation();
  }, [cancelNavigation]);

  const handleBackClick = useCallback(() => {
    if (!propertyData) return;
    // Go back directly - uploaded files remain on the listing
    setCurrentStep?.((prev) => prev - 1);
  }, [propertyData, setCurrentStep]);

  if (!propertyData) {
    setTimeout(() => {
      window.location.href = "/listings";
    }, 3000);
    return <LoadingScreen />;
  }

  return (
    <>
     {planLimitOpen && <PlanLimitReached
        open={planLimitOpen}
        onClose={() => {
          setPlanLimitOpen(false);
          navigate("/listings");
        }}
        type="listing"
      />}

      <UploadNavigationWarning
        isOpen={showNavigationWarning}
        onClose={handleStayOnPage}
        onLeave={handleLeaveWithUploads}
        onStay={handleStayOnPage}
      />

      {/* image upload warning pop up  */}
       <ReconfirmOverlay
        isOpen={showMissingImagesWarning}
        onClose={() => setShowMissingImagesWarning(false)}
        onConfirm={handleUploadImages}
        cancelClose={handleProceedWithoutImages}
        title="Please upload images"
        message="Please upload images of the property, or you will not be able to generate a marketing campaign."
        confirmText="Yes, Upload Images"
        cancelText="No, Continue"
      />

      <main
        id="media-adder"
        className={`flex overflow-auto flex-col justify-center items-center min-h-full shadow-[0px_4px_4px_rgba(0,0,0,0.25)] ${
          isMobile ? "px-0 bg-white" : "bg-background"
        }`}
      >
      <div
        className={`bg-white ${
          isMobile ? "w-[90%]" : "w-[60%] rounded-lg"
        } py-10`}
      >
        <h1 className="mb-6 text-primary text-3xl font-bold leading-none text-center">
          Add Images & Videos
        </h1>
        <div className={`flex flex-col`}>
          {/* Left Column */}
          <div className={!isMobile ? "px-20" : ""}>
            {/* Drag & Drop Upload (MB-1139 new flow) */}
            <DragAndDropMedia
              type="listing"
              listingId={propertyData._id}
              onUploadComplete={async () => {
                await refreshListing();
              }}
              mlsApproved={mlsApproved || false}
              isUploading={isUploading}
              setIsUploading={setIsUploading}
              listingType={propertyData.listingType}
              isSyncing={propertyData.isSyncing}
              hideNotes={true}
              onActiveUploadsChange={setActiveUploads}
              onImageTagLimitExceeded={() => setPlanLimitOpen(true)}
              existingMedia={{
                images: propertyData.images || [],
                videos: propertyData.videos || [],
              }}
            />

              {/* Actions */}
              <div className="mt-6 flex justify-between items-center">
                <Button 
                  onClick={handleBackClick}
                  disabled={activeUploads > 0 || isUploading}
                  className={activeUploads > 0 || isUploading ? "opacity-50 cursor-not-allowed" : ""}
                >
                  Back
                </Button>
                <Button 
                  onClick={handleFinishClick}
                  disabled={activeUploads > 0 || isUploading}
                  className={activeUploads > 0 || isUploading ? "opacity-50 cursor-not-allowed" : ""}
                >
                  {/* {activeUploads > 0 || isUploading ? "Uploading..." : "Finish"} */}
                  Finish
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default MediaAdder;
